import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Popover,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Button,
  Tooltip,
  Stack,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { useTranslation } from 'react-i18next';
import { useTableSettings, useSaveTableSettings, type TableSettings } from '../../api/hooks/useTableSettings';
import { exportToCsv } from '../../utils/csvExport';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ColumnDef<T> {
  id: string;
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  sortable?: boolean;
  filterable?: boolean;
  filterType?: 'text' | 'select' | 'multiselect' | 'date' | 'number';
  filterOptions?: { value: string; label: string }[];
  width?: number;
  minWidth?: number;
  hideable?: boolean;
  /** Bei true ist die Spalte initial ausgeblendet (per Spaltenauswahl einblendbar). */
  defaultHidden?: boolean;
  render?: (value: unknown, row: T) => React.ReactNode;
}

export interface EmptyStateConfig {
  title: string;
  hint?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export interface EnhancedDataTableProps<T> {
  tableId: string;
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  /** Server-Pagination aktiv: totalCount stammt vom Server, page wird per onPageChange angefordert. */
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  onRowClick?: (row: T) => void;
  exportFilename?: string;
  getRowId?: (row: T) => string;
  error?: string | null;
  /** Optionaler kontextspezifischer Empty-State. Fallback bei null/undefined: zentral t('table.noData'). */
  emptyState?: EmptyStateConfig;
  /** Pagination-Modus. 'offset' (Default) nutzt klassische TablePagination. 'cursor' nutzt Prev/Next-Buttons. */
  paginationMode?: 'offset' | 'cursor';
  /** Cursor-Mode: Caller signalisiert ob naechste Seite vorhanden. */
  hasMore?: boolean;
  /** Cursor-Mode: Caller signalisiert ob Zurueck-Navigation moeglich (typisch: cursorStack.length > 0). */
  canGoBack?: boolean;
  /** Cursor-Mode: Naechste Seite anfordern. Caller pushed neuen Cursor + reload. */
  onNextPage?: () => void;
  /** Cursor-Mode: Zurueck-Navigation. Caller popped Cursor + reload. */
  onPrevPage?: () => void;
  /** Cursor-Mode: Aktuelle Page-Size (vom Caller verwaltet, da Server-seitiges limit). */
  cursorPageSize?: number;
  /** Cursor-Mode: Page-Size-Wechsel — Caller setzt limit + reset Cursor-Stack. */
  onCursorPageSizeChange?: (size: number) => void;
  /** Cursor-Mode: Aktuelle Seitenzahl (1-basiert) — wird in der Footer-Anzeige verwendet. */
  cursorPage?: number;
  /** Server-Filter: Wenn gesetzt, deaktiviert client-side Filterung. Caller bekommt
   *  Filter-Aenderungen via Callback und reicht sie als Query-Params an die API durch. */
  serverFilters?: boolean;
  /** Server-Filter: Callback bei Filter-Aenderung — kompletter Filter-Snapshot. */
  onFiltersChange?: (filters: Record<string, string | string[]>) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCellValue<T>(row: T, accessor: keyof T | ((row: T) => React.ReactNode)): unknown {
  if (typeof accessor === 'function') return accessor(row);
  return (row as Record<string, unknown>)[accessor as string];
}

function getCellStringValue<T>(row: T, accessor: keyof T | ((row: T) => React.ReactNode)): string {
  const val = getCellValue(row, accessor);
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  return '';
}

/** Spaltenauswahl je Benutzer und Tabelle im localStorage (Zugriff abgesichert). */
const LS_COLUMNS_PREFIX = 'phq-columns:';

function loadStoredColumns(tableId: string): Set<string> | null {
  try {
    const raw = localStorage.getItem(LS_COLUMNS_PREFIX + tableId);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((k): k is string => typeof k === 'string')) : null;
  } catch {
    return null;
  }
}

function storeColumns(tableId: string, ids: string[]): void {
  try {
    localStorage.setItem(LS_COLUMNS_PREFIX + tableId, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EnhancedDataTable<T>({
  tableId,
  columns,
  data,
  loading = false,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onRowClick,
  exportFilename,
  getRowId,
  error,
  emptyState,
  paginationMode = 'offset',
  hasMore = false,
  canGoBack = false,
  onNextPage,
  onPrevPage,
  cursorPageSize,
  onCursorPageSizeChange,
  cursorPage,
  serverFilters = false,
  onFiltersChange,
}: EnhancedDataTableProps<T>) {
  const { t } = useTranslation();

  // ---- Persisted settings ----
  const { data: settingsResp } = useTableSettings(tableId);
  const { saveDebounced } = useSaveTableSettings(tableId);
  const persisted = settingsResp?.data;

  // ---- Local state (initialised from persisted when available) ----
  const [visibleCols, setVisibleCols] = useState<Set<string> | null>(() => loadStoredColumns(tableId));
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [initialised, setInitialised] = useState(false);

  // Populate local state from persisted settings once
  useEffect(() => {
    if (persisted && !initialised) {
      if (Array.isArray(persisted.visible_columns) && persisted.visible_columns.length > 0) {
        setVisibleCols(new Set(persisted.visible_columns));
      }
      if (persisted.sort_by) setSortBy(persisted.sort_by);
      if (persisted.sort_order === 'asc' || persisted.sort_order === 'desc') setSortOrder(persisted.sort_order);
      if (persisted.filters && Object.keys(persisted.filters).length > 0) setFilters(persisted.filters);
      if (persisted.page_size) setPageSize(persisted.page_size);
      setInitialised(true);
    }
    // Kein persisted vorhanden -> trotzdem als initialisiert markieren
    if (!persisted && settingsResp && !initialised) {
      setInitialised(true);
    }
  }, [persisted, settingsResp, initialised]);

  // ---- Derived visible columns ----
  // hideable:false-Spalten (z.B. Actions-Spalte mit Delete-Icon) müssen IMMER sichtbar
  // sein — auch wenn persisted Settings aus älteren UI-Versionen sie nicht enthalten.
  const effectiveVisibleCols = useMemo(() => {
    const forced = new Set(columns.filter((c) => c.hideable === false).map((c) => c.id));
    if (visibleCols) {
      const merged = new Set(visibleCols);
      forced.forEach((id) => merged.add(id));
      return merged;
    }
    return new Set(columns.filter((c) => !c.defaultHidden).map((c) => c.id));
  }, [visibleCols, columns]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => effectiveVisibleCols.has(c.id)),
    [columns, effectiveVisibleCols],
  );

  // ---- Persist helper ----
  const persistSettings = useCallback(
    (overrides: Partial<TableSettings>) => {
      const current: Partial<TableSettings> = {
        visible_columns: [...effectiveVisibleCols],
        sort_by: sortBy,
        sort_order: sortOrder,
        filters,
        page_size: pageSize,
        ...overrides,
      };
      saveDebounced(current);
    },
    [effectiveVisibleCols, sortBy, sortOrder, filters, pageSize, saveDebounced],
  );

  // ---- Sorting ----
  const handleSort = useCallback(
    (colId: string) => {
      let newOrder: 'asc' | 'desc' = 'asc';
      if (sortBy === colId) {
        newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      }
      setSortBy(colId);
      setSortOrder(newOrder);
      onSortChange?.(colId, newOrder);
      persistSettings({ sort_by: colId, sort_order: newOrder });
    },
    [sortBy, sortOrder, onSortChange, persistSettings],
  );

  // ---- Filtering ----
  const handleFilterChange = useCallback(
    (colId: string, value: string | string[]) => {
      const updated = { ...filters, [colId]: value };
      const isEmpty = value == null || value === '' || (Array.isArray(value) && value.length === 0);
      if (isEmpty) delete updated[colId];
      setFilters(updated);
      setPage(0);
      persistSettings({ filters: updated });
      if (serverFilters) onFiltersChange?.(updated);
    },
    [filters, persistSettings, serverFilters, onFiltersChange],
  );

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setPage(0);
    persistSettings({ filters: {} });
    if (serverFilters) onFiltersChange?.({});
  }, [persistSettings, serverFilters, onFiltersChange]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  // ---- Client-side filtering (when no server handler) ----
  const filteredData = useMemo(() => {
    if (serverFilters) return data; // Server hat bereits gefiltert
    if (Object.keys(filters).length === 0) return data;
    return data.filter((row) =>
      Object.entries(filters).every(([colId, filterVal]) => {
        if (filterVal == null || filterVal === '' || (Array.isArray(filterVal) && filterVal.length === 0)) return true;
        const col = columns.find((c) => c.id === colId);
        if (!col) return true;
        const cellStr = getCellStringValue(row, col.accessor);
        if (Array.isArray(filterVal)) {
          // Multi-select: exakter Match auf einen der Werte
          return filterVal.some((v) => cellStr === v);
        }
        return cellStr.toLowerCase().includes(filterVal.toLowerCase());
      }),
    );
  }, [data, filters, columns]);

  // ---- Client-side sorting (when no server handler) ----
  const sortedData = useMemo(() => {
    if (!sortBy || onSortChange) return filteredData; // Server-side sort when handler present
    const col = columns.find((c) => c.id === sortBy);
    if (!col) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = getCellStringValue(a, col.accessor);
      const bVal = getCellStringValue(b, col.accessor);
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortBy, sortOrder, columns, onSortChange]);

  // ---- Pagination ----
  const effectiveTotal = totalCount ?? sortedData.length;
  const pagedData = totalCount !== undefined ? sortedData : sortedData.slice(page * pageSize, (page + 1) * pageSize);

  const handlePageChange = useCallback(
    (_e: unknown, newPage: number) => {
      setPage(newPage);
      onPageChange?.(newPage);
    },
    [onPageChange],
  );

  const handlePageSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const size = parseInt(e.target.value, 10);
      setPageSize(size);
      setPage(0);
      onPageSizeChange?.(size);
      persistSettings({ page_size: size });
    },
    [onPageSizeChange, persistSettings],
  );

  // ---- Column visibility popover ----
  const [colAnchor, setColAnchor] = useState<HTMLElement | null>(null);

  const toggleColumn = useCallback(
    (colId: string) => {
      const next = new Set(effectiveVisibleCols);
      if (next.has(colId)) {
        if (next.size > 1) next.delete(colId);
      } else {
        next.add(colId);
      }
      setVisibleCols(next);
      storeColumns(tableId, [...next]);
      persistSettings({ visible_columns: [...next] });
    },
    [effectiveVisibleCols, persistSettings, tableId],
  );

  // ---- CSV Export ----
  const handleExport = useCallback(async () => {
    if (!exportFilename) return;
    const exportCols = visibleColumns
      .map((c) => ({
        header: c.header,
        accessor: typeof c.accessor === 'string' ? c.accessor : c.id,
      }));
    const exportData = sortedData.map((row) => {
      const obj: Record<string, unknown> = {};
      for (const col of visibleColumns) {
        const key = typeof col.accessor === 'string' ? col.accessor : col.id;
        obj[key] = getCellStringValue(row, col.accessor);
      }
      return obj;
    });
    try {
      await exportToCsv(exportFilename, exportCols, exportData);
    } catch (err) {
      console.error('[CSV-Export]', err);
    }
  }, [exportFilename, visibleColumns, sortedData]);

  // ---- Error state ----
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
        {hasActiveFilters && (
          <Tooltip title={t('table.clearFilter')}>
            <IconButton size="small" onClick={clearAllFilters}>
              <FilterListOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('table.columns')}>
          <IconButton size="small" onClick={(e) => setColAnchor(e.currentTarget)}>
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {exportFilename && (
          <Button
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
          >
            {t('table.export')}
          </Button>
        )}
      </Box>

      {/* Column visibility popover */}
      <Popover
        open={Boolean(colAnchor)}
        anchorEl={colAnchor}
        onClose={() => setColAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('table.columns')}</Typography>
          <Stack>
            {columns
              .filter((c) => c.hideable !== false)
              .map((col) => (
                <FormControlLabel
                  key={col.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={effectiveVisibleCols.has(col.id)}
                      onChange={() => toggleColumn(col.id)}
                    />
                  }
                  label={col.header}
                />
              ))}
          </Stack>
        </Box>
      </Popover>

      <TableContainer>
        <Table size="small">
          <TableHead>
            {/* Header row */}
            <TableRow>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.id}
                  style={{ minWidth: col.minWidth, width: col.width }}
                >
                  {col.sortable !== false ? (
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortOrder : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.header}
                    </TableSortLabel>
                  ) : (
                    col.header
                  )}
                </TableCell>
              ))}
            </TableRow>
            {/* Filter row */}
            {visibleColumns.some((c) => c.filterable !== false) && (
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableCell key={`filter-${col.id}`} sx={{ py: 0.5, px: 1 }}>
                    {col.filterable !== false ? (
                      col.filterType === 'multiselect' && col.filterOptions ? (
                        <Select
                          size="small"
                          fullWidth
                          multiple
                          displayEmpty
                          value={Array.isArray(filters[col.id]) ? (filters[col.id] as string[]) : []}
                          onChange={(e) => handleFilterChange(col.id, e.target.value as string[])}
                          variant="standard"
                          renderValue={(selected) => {
                            const arr = selected as string[];
                            if (arr.length === 0) return <span style={{ color: 'var(--pm-fg-subtle)' }}>—</span>;
                            return arr
                              .map((v) => col.filterOptions!.find((o) => o.value === v)?.label ?? v)
                              .join(', ');
                          }}
                        >
                          {col.filterOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Checkbox checked={Array.isArray(filters[col.id]) && (filters[col.id] as string[]).includes(opt.value)} size="small" />
                              <ListItemText primary={opt.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      ) : col.filterType === 'select' && col.filterOptions ? (
                        <Select
                          size="small"
                          fullWidth
                          displayEmpty
                          value={typeof filters[col.id] === 'string' ? (filters[col.id] as string) : ''}
                          onChange={(e) => handleFilterChange(col.id, e.target.value)}
                          variant="standard"
                        >
                          <MenuItem value="">&mdash;</MenuItem>
                          {col.filterOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          variant="standard"
                          placeholder={t('table.filter')}
                          value={typeof filters[col.id] === 'string' ? (filters[col.id] as string) : ''}
                          onChange={(e) => handleFilterChange(col.id, e.target.value)}
                        />
                      )
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : pagedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 6 }}>
                  {emptyState ? (
                    <Stack alignItems="center" spacing={1}>
                      {emptyState.icon}
                      <Typography variant="subtitle1" fontWeight={600}>
                        {emptyState.title}
                      </Typography>
                      {emptyState.hint && (
                        <Typography variant="body2" color="text.secondary">
                          {emptyState.hint}
                        </Typography>
                      )}
                      {emptyState.action && <Box sx={{ mt: 1 }}>{emptyState.action}</Box>}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">{t('table.noData')}</Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              pagedData.map((row, index) => (
                <TableRow
                  hover
                  key={getRowId ? getRowId(row) : index}
                  onClick={() => onRowClick?.(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {visibleColumns.map((col) => {
                    const raw = getCellValue(row, col.accessor);
                    return (
                      <TableCell key={col.id}>
                        {col.render ? col.render(raw, row) : String(raw ?? '')}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paginationMode === 'cursor' ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, px: 2, py: 1.5, flexWrap: 'wrap' }}>
          {onCursorPageSizeChange && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('table.rowsPerPage', { defaultValue: 'Pro Seite:' })}
              </Typography>
              <Select
                size="small"
                value={cursorPageSize ?? 25}
                onChange={(e) => onCursorPageSizeChange(Number(e.target.value))}
                variant="standard"
                sx={{ minWidth: 64 }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            {typeof totalCount === 'number'
              ? t('table.cursorTotal', {
                  shown: pagedData.length,
                  total: totalCount,
                  defaultValue: '{{shown}} von {{total}} angezeigt',
                })
              : t('table.cursorPage', {
                  page: cursorPage ?? 1,
                  count: pagedData.length,
                  defaultValue: 'Seite {{page}} · {{count}} Einträge',
                })}
          </Typography>
          <Button size="small" onClick={onPrevPage} disabled={!canGoBack || loading}>
            {t('table.prev', { defaultValue: 'Zurück' })}
          </Button>
          <Button size="small" onClick={onNextPage} disabled={!hasMore || loading} variant="outlined">
            {t('table.next', { defaultValue: 'Weiter' })}
          </Button>
        </Box>
      ) : (
        <TablePagination
          component="div"
          count={effectiveTotal}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('table.rowsPerPage')}
        />
      )}
    </Paper>
  );
}
